import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { formatTimestamp, hexToRgb } from './helpers';

/**
 * Génère et télécharge un PDF de la chorégraphie de manière asynchrone
 */
export async function generateChoreographyPDF({
  currentSession,
  dancers,
  periods,
  skills,
  canvasPixelWidth,
  canvasPixelHeight,
  drawGrid,
  drawPosition,
  gridColumns = 28,
  gridRows = 18,
}) {
  // Démarrer le traitement de manière asynchrone pour ne pas bloquer l'UI
  await new Promise(resolve => setTimeout(resolve, 0));
  
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "pt",
    format: "a4",
    compress: true,
  });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 40;

  const drawPageFrame = () => {
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(2);
    doc.rect(margin / 2, margin / 2, pageWidth - margin, pageHeight - margin);
  };

  const drawLogoTopRight = () => {
    doc.setFont("Shirkhand", "normal");
    doc.setFontSize(20);
    doc.setTextColor(255, 71, 126);
    doc.text("Dansy", pageWidth - margin, margin + 20, { align: "right" });
    doc.setTextColor(0, 0, 0);
  };

  // Page 1 : Couverture
  doc.setFillColor(245, 245, 245);
  doc.rect(0, 0, pageWidth, pageHeight, "F");
  drawPageFrame();
  drawLogoTopRight();

  doc.setFont("helvetica", "bold");
  doc.setFontSize(36);
  doc.text("Présentation de la chorégraphie", pageWidth / 2, pageHeight / 2 - 80, { align: "center" });
  doc.setFontSize(20);
  doc.setFont("helvetica", "normal");
  doc.text(`Session : ${currentSession.name}`, pageWidth / 2, pageHeight / 2 - 40, { align: "center" });
  doc.text(`Danseurs : ${dancers.length}`, pageWidth / 2, pageHeight / 2 - 10, { align: "center" });
  doc.text(`Périodes : ${periods.length}`, pageWidth / 2, pageHeight / 2 + 20, { align: "center" });
  doc.text(`Exporté le : ${new Date().toLocaleString()}`, pageWidth / 2, pageHeight / 2 + 50, { align: "center" });

  // Page 2 : Tableau
  doc.addPage();
  drawPageFrame();
  drawLogoTopRight();

  doc.setFont("helvetica", "bold");
  doc.setFontSize(24);
  doc.text("Caractéristiques générales :", pageWidth / 2, margin + 20, { align: "center" });

  const periodsList = periods.map(
    (period) =>
      `${period.name} (${formatTimestamp(period.start_time)} > ${formatTimestamp(period.end_time)})`
  );
  const dancersList = dancers.map((dancer) => dancer.name);
  const skillsArray = skills.map((skill) => skill);
  const maxLength = Math.max(periodsList.length, dancersList.length, skillsArray.length);
  while (periodsList.length < maxLength) periodsList.push("");
  while (dancersList.length < maxLength) dancersList.push("");
  while (skillsArray.length < maxLength) skillsArray.push(null);

  const colWidth = (pageWidth - 2 * margin) / 3;
  let startY = 100;
  const defaultRowHeight = 12;
  const availableHeight = pageHeight - margin - startY;
  let rowHeight = defaultRowHeight;
  if (maxLength * defaultRowHeight > availableHeight) {
    rowHeight = availableHeight / maxLength;
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("Périodes", margin, startY);
  doc.text("Danseurs", margin + colWidth, startY);
  doc.text("Skills", margin + 2 * colWidth, startY);
  startY += rowHeight + 4;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);

  for (let i = 0; i < maxLength; i++) {
    const currentY = startY + i * rowHeight;
    doc.text(periodsList[i], margin, currentY);
    doc.text(dancersList[i], margin + colWidth, currentY);

    const skill = skillsArray[i];
    if (skill) {
      const [r, g, b] = hexToRgb(skill.color);
      doc.setTextColor(r, g, b);
      doc.text(skill.name, margin + 2 * colWidth, currentY);
      doc.setTextColor(0, 0, 0);
    }
  }

  // Pages pour chaque période (traitement asynchrone pour éviter de bloquer l'UI)
  for (const period of periods) {
    // Laisser respirer le navigateur entre chaque page
    await new Promise(resolve => setTimeout(resolve, 0));
    
    doc.addPage();
    drawPageFrame();
    drawLogoTopRight();

    doc.setFont("helvetica", "bold");
    doc.setFontSize(24);
    doc.text(`Période : ${period.name}`, pageWidth / 2, margin + 20, { align: "center" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(14);
    doc.text(
      `De ${formatTimestamp(period.start_time)} à ${formatTimestamp(period.end_time)}`,
      pageWidth / 2,
      margin + 40,
      { align: "center" }
    );

    const offscreenCanvas = document.createElement("canvas");
    offscreenCanvas.width = canvasPixelWidth;
    offscreenCanvas.height = canvasPixelHeight;
    const ctx = offscreenCanvas.getContext("2d");

    drawGrid(ctx, canvasPixelWidth, canvasPixelHeight, periods, gridColumns, gridRows);
    if (period.positions && period.positions.length > 0) {
      period.positions.forEach((pos) => drawPosition(ctx, pos, canvasPixelWidth, canvasPixelHeight, null, dancers, false, true));
    }

    const imgWidth = pageWidth - 2 * margin;
    const imgHeight = pageHeight - 2 * margin - 60;
    const imgX = margin;
    const imgY = margin + 60;
    const canvasImage = offscreenCanvas.toDataURL("image/png");
    doc.addImage(canvasImage, "PNG", imgX, imgY, imgWidth, imgHeight);
    doc.setDrawColor(150, 150, 150);
    doc.setLineWidth(1);
    doc.roundedRect(imgX, imgY, imgWidth, imgHeight, 10, 10, "S");
  }

  // Dernière page
  doc.addPage();
  doc.setFillColor(245, 245, 245);
  doc.rect(0, 0, pageWidth, pageHeight, "F");
  drawPageFrame();
  drawLogoTopRight();

  const closingCenterY = pageHeight / 2;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(36);
  doc.setTextColor(0, 0, 0);
  doc.text("Merci !", pageWidth / 2, closingCenterY - 20, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(20);
  doc.setTextColor(0, 0, 0);
  doc.text("Réalisé sur www.dansy.net", pageWidth / 2, closingCenterY + 10, { align: "center" });
  doc.setTextColor(0, 0, 0);

  doc.save(`${currentSession.name || "export"}.pdf`);
}
